import React from 'react';

export type Accent = 'brand' | 'teal' | 'violet' | 'amber' | 'rose' | 'emerald';

const ACCENTS: Record<Accent, string> = {
  brand: 'bg-[#E8F4FF] text-[#035DB8] dark:bg-sky-400/10 dark:text-sky-300',
  teal: 'bg-[#e7faf6] text-[#087f70] dark:bg-teal-400/10 dark:text-teal-300',
  violet: 'bg-[#f1edff] text-[#6d4bd1] dark:bg-violet-400/10 dark:text-violet-300',
  amber: 'bg-[#fff5df] text-[#a96008] dark:bg-amber-400/10 dark:text-amber-300',
  rose: 'bg-[#fff0f2] text-[#c43d5b] dark:bg-rose-400/10 dark:text-rose-300',
  emerald: 'bg-[#e9f9f1] text-[#13815a] dark:bg-emerald-400/10 dark:text-emerald-300',
};

const SIZES = {
  sm: 'h-8 w-8 rounded-[8px]',
  md: 'h-11 w-11 rounded-[12px]',
  lg: 'h-14 w-14 rounded-[16px]',
};

export const IconTile: React.FC<{
  accent?: Accent;
  size?: keyof typeof SIZES;
  className?: string;
  children: React.ReactNode;
}> = ({ accent = 'brand', size = 'md', className = '', children }) => (
  <span aria-hidden="true" className={`inline-flex flex-shrink-0 items-center justify-center ${SIZES[size]} ${ACCENTS[accent]} ${className}`}>
    {children}
  </span>
);

export const StepHeading = React.forwardRef<HTMLHeadingElement, {
  icon: React.ReactNode;
  accent?: Accent;
  eyebrow?: string;
  title: string;
  subtitle?: string;
}>(({ icon, accent = 'brand', eyebrow, title, subtitle }, ref) => (
  <div className="mb-7 flex items-start gap-4 sm:mb-8">
    <IconTile accent={accent} size="lg">{icon}</IconTile>
    <div className="min-w-0 pt-0.5">
      {eyebrow && <p className="mb-1 text-xs font-bold uppercase tracking-[0.14em] text-[#035DB8] dark:text-sky-300">{eyebrow}</p>}
      <h1 ref={ref} tabIndex={-1} className="scroll-mb-28 rounded-sm text-[26px] font-extrabold leading-[1.15] tracking-[-0.03em] text-[#0B2F63] outline-none sm:text-[30px]">{title}</h1>
      {subtitle && <p className="mt-2 max-w-2xl text-[14px] leading-6 text-[#64748B] sm:text-[15px] dark:text-slate-300">{subtitle}</p>}
    </div>
  </div>
));

StepHeading.displayName = 'StepHeading';

export const Surface: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <div className={`rounded-[20px] border border-[#dce6ee] bg-white shadow-[var(--patient-shadow-2)] dark:border-white/[0.09] dark:bg-[#13243a] dark:shadow-none ${className}`}>
    {children}
  </div>
);

export const SidePanel: React.FC<{
  label?: string;
  icon?: React.ReactNode;
  accent?: Accent;
  tone?: 'plain' | 'tinted';
  className?: string;
  children: React.ReactNode;
}> = ({ label, icon, accent = 'brand', tone = 'plain', className = '', children }) => (
  <div className={`rounded-[16px] border p-4 ${tone === 'tinted' ? 'border-brand-500/[0.08] bg-brand-50/50 dark:border-white/[0.08] dark:bg-white/[0.035]' : 'border-brand-500/[0.08] bg-white dark:border-white/[0.09] dark:bg-[#13243a]'} ${className}`}>
    {(label || icon) && (
      <div className="mb-3 flex items-center gap-2.5">
        {icon && <IconTile accent={accent} size="sm">{icon}</IconTile>}
        {label && <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#52677e] dark:text-slate-300">{label}</p>}
      </div>
    )}
    {children}
  </div>
);

export const FieldLabel: React.FC<{ htmlFor?: string; required?: boolean; optional?: boolean; children: React.ReactNode }> = ({ htmlFor, required, optional, children }) => (
  <label htmlFor={htmlFor} className="mb-2 block text-[13px] font-semibold leading-[18px] text-[#42566e] dark:text-slate-300">
    {children}{required && <span className="ml-0.5 text-rose-500">*</span>}{optional && <span className="ml-1.5 font-normal text-slate-400">optional</span>}
  </label>
);

export const inputClass = [
  'h-12 w-full rounded-[12px] border border-[#dce6ee] bg-white px-4 text-[14px] text-[#0F172A]',
  'outline-none placeholder:text-[#94A3B8] focus:border-[#035DB8] focus:ring-4 focus:ring-[#035DB8]/[0.08]',
  'dark:border-white/[0.14] dark:bg-[#182d48] dark:text-white dark:focus:border-sky-400',
].join(' ');
